/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'
import { FormattedMessage } from 'react-intl'
import { groupBy, sortBy, keyBy, isEmpty, mapValues } from 'lodash'

import {
  EuiCallOut,
  EuiFlexGrid,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from '@elastic/eui'

import type {
  SliderInstanceType,
  StackDeployment,
  InstanceSummary,
  NodesVisualizationFilters,
} from '@modules/ui-types'
import { withErrorBoundary } from '@modules/cui/Boundary'
import type { WithPermissionsProps } from '@modules/permissions-components/PermissionsGate/withPermissions'

import { getDeploymentTopologyInstances } from '@/lib/stackDeployments/selectors/instances'

import DocLink from '../../DocLink'

import NodeInstanceVisualization from './NodeInstanceVisualization'
import StackDeploymentNodesVisualizationFilters, {
  applyFilters,
  hasFilters,
} from './StackDeploymentNodesVisualizationFilters'
import InstancesTable from './InstancesTableViz'

import type { ReactNode } from 'react'

export type Props = {
  title: ReactNode
  deployment: StackDeployment
  fetchDeploymentAllocators: () => void
  fetchNodeStats: () => void
  showNativeMemoryPressure?: boolean
  sliderInstanceType?: SliderInstanceType
  disableNodeControlsIfPlanPending?: boolean
  shouldFetchDeploymentAllocators: boolean
} & WithPermissionsProps<'hasSearchAllocatorsPermission'>

interface State {
  filters: NodesVisualizationFilters
  viewType: 'Grid' | 'Table'
}

class StackDeploymentNodesVisualization extends Component<Props, State> {
  state: State = {
    filters: {},
    viewType: `Grid`,
  }

  componentDidMount() {
    const { fetchNodeStats, deployment, sliderInstanceType } = this.props

    const instanceSummaries = getDeploymentTopologyInstances({ deployment, sliderInstanceType })

    if (
      instanceSummaries.some((instanceSummary) =>
        instanceSummary.instance.node_roles?.includes(`data_frozen`),
      )
    ) {
      // we consume this later, in `<FrozenNodeUsage>`
      fetchNodeStats()
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.shouldFetchDeploymentAllocators &&
      // authorization request has been resolved and user is authorized
      // to search for allocators
      !prevProps.permissions.hasSearchAllocatorsPermission &&
      this.props.permissions.hasSearchAllocatorsPermission
    ) {
      this.props.fetchDeploymentAllocators()
    }
  }

  render() {
    const { zoneIds, zoneSummaries } = this.getSummariesPerZone()

    return (
      <Fragment>
        {this.renderHeader()}

        {this.renderMemoryPressureWarning()}

        {this.state.viewType === `Grid` ? (
          this.renderZonesGrid({ zoneIds, zoneSummaries })
        ) : (
          <InstancesTable
            zoneIds={zoneIds}
            zoneSummaries={zoneSummaries}
            deployment={this.props.deployment}
            showNativeMemoryPressure={this.props.showNativeMemoryPressure}
          />
        )}
      </Fragment>
    )
  }

  renderHeader() {
    const { title } = this.props

    return (
      <Fragment>
        <EuiFlexGroup gutterSize='m' alignItems='center' justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>{title}</EuiFlexItem>

          <EuiFlexItem>{this.renderFilters()}</EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size='m' />
      </Fragment>
    )
  }

  renderFilters() {
    const { deployment, sliderInstanceType } = this.props
    const { filters } = this.state

    const instanceSummaries = getDeploymentTopologyInstances({ deployment, sliderInstanceType })

    return (
      <EuiFlexGroup gutterSize='xs' alignItems='center' justifyContent='flexEnd'>
        <EuiFlexItem grow={false}>
          <StackDeploymentNodesVisualizationFilters
            instanceSummaries={instanceSummaries}
            filters={filters}
            onChange={this.setFilters}
            onChangeViewType={this.changeViewType}
            currentViewType={this.state.viewType}
          />
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  renderMemoryPressureWarning() {
    const { showNativeMemoryPressure } = this.props

    if (!showNativeMemoryPressure) {
      return null
    }

    return (
      <Fragment>
        <EuiCallOut
          title='At least one Elasticsearch node has JVM memory pressure over 75%'
          color='warning'
          iconType='alert'
        >
          <FormattedMessage
            id='nodes-visualization.jvm-memory-pressure-description'
            defaultMessage='This can affect your cluster stability. Consider reducing the number of shards, optimizing large queries, or increasing your cluster size. {learnMore}.'
            values={{
              learnMore: <DocLink link='jvmMemoryPressure'>Learn more</DocLink>,
            }}
          />
        </EuiCallOut>

        <EuiSpacer size='m' />
      </Fragment>
    )
  }

  renderZonesGrid({
    zoneIds,
    zoneSummaries,
  }: {
    zoneIds: string[]
    zoneSummaries: {
      [key: string]: InstanceSummary[]
    }
  }) {
    return (
      <EuiFlexGrid columns={3}>
        {zoneIds.map((zoneId) =>
          this.renderZone({
            zoneId,
            // The code is byzantine, but ultimately we know that we won't get undefined here
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            zoneSummaries: zoneSummaries[zoneId]!,
          }),
        )}
      </EuiFlexGrid>
    )
  }

  renderZone({ zoneId, zoneSummaries }: { zoneId: string; zoneSummaries: InstanceSummary[] }) {
    return (
      <EuiPanel key={zoneId} paddingSize='m' hasShadow={false} color='subdued'>
        {this.renderZoneTitle({ zoneId })}

        <EuiSpacer size='l' />

        <div className='node-visualization-instance'>
          {this.renderZoneFilteredWarning({ zoneId, zoneSummaries })}
          {this.renderZoneSummaries({ zoneSummaries })}
        </div>
      </EuiPanel>
    )
  }

  renderZoneTitle({ zoneId }: { zoneId: string }) {
    return (
      <EuiTitle size='xxs'>
        <h3 className='nodes-visualization-column-title'>
          <EuiFlexGroup gutterSize='xs' alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiText size='s'>
                <FormattedMessage id='nodes-visualization.zone' defaultMessage='Zone' />
              </EuiText>
            </EuiFlexItem>

            <EuiFlexItem>
              {zoneId === `undefined` ? (
                <FormattedMessage
                  id='nodes-visualization.zone-untitled'
                  defaultMessage='Unassigned zone'
                />
              ) : (
                zoneId
              )}
            </EuiFlexItem>
          </EuiFlexGroup>
        </h3>
      </EuiTitle>
    )
  }

  renderZoneFilteredWarning({
    zoneId,
    zoneSummaries,
  }: {
    zoneId: string
    zoneSummaries: InstanceSummary[]
  }) {
    const { filters } = this.state

    if (!hasFilters({ filters }) || !isEmpty(zoneSummaries)) {
      return null
    }

    return (
      <EuiText color='subdued' size='s'>
        <FormattedMessage
          id='nodes-visualization.no-matches-in-zone'
          defaultMessage='No matches in {zoneId} for your filters.'
          values={{ zoneId }}
        />
      </EuiText>
    )
  }

  renderZoneSummaries({ zoneSummaries }: { zoneSummaries: InstanceSummary[] }) {
    const { deployment, showNativeMemoryPressure } = this.props

    return (
      <Fragment>
        {zoneSummaries.map((summary, index) => {
          const name = this.getInstanceSummaryName(summary)

          return (
            <Fragment key={`${summary.kind}-${name}-${index}`}>
              <NodeInstanceVisualization
                deployment={deployment}
                instanceSummary={summary}
                showNativeMemoryPressure={showNativeMemoryPressure}
              />

              <EuiSpacer size='m' />
            </Fragment>
          )
        })}
      </Fragment>
    )
  }

  getSummariesPerZone() {
    const { deployment, sliderInstanceType } = this.props
    const { filters } = this.state

    const instanceSummaries = getDeploymentTopologyInstances({ deployment, sliderInstanceType })

    const zoneGroups = groupBy(instanceSummaries, `instance.zone`)
    const zoneIds = Object.keys(zoneGroups).sort()

    const zoneSummaries = mapValues(keyBy(zoneIds), (zone) =>
      applyFilters({
        instanceSummaries: sortBy(zoneGroups[zone], [
          `kind`,
          `instance.instance_configuration.name`,
          `instance.instance_name`,
        ]),
        filters,
      }),
    )

    return {
      zoneIds,
      zoneSummaries,
    }
  }

  getInstanceSummaryName(instanceSummary: InstanceSummary) {
    return instanceSummary.instance.instance_configuration?.name ?? ''
  }

  setFilters = (filters: NodesVisualizationFilters) => {
    this.setState({ filters })
  }

  changeViewType = (viewType) => {
    this.setState({ viewType })
  }
}

export default withErrorBoundary(StackDeploymentNodesVisualization)
